/**
 * 邮件发送配置系统
 *
 * 设计理念：
 * 1. 统一领域模型：所有邮件相关概念使用一致命名
 * 2. 配置驱动：支持灵活扩展，避免硬编码
 * 3. 类型安全：通过常量定义所有可能的配置项
 *
 * 最佳实践参考：
 * - React官方文档：受控组件和不可变数据模式
 * - Ant Design表单组件：配置化选项系统
 * - 企业级邮件系统：Gmail/Outlook的发送选项抽象
 */

// ============ 邮件类型常量 ============

/**
 * 邮件类型枚举
 */
export const EmailType = {
  CONFIRMATION: 'confirmation',
  PUBLISH: 'publish',
  GENERAL: 'general',  // 通用邮件（用户自由选择模板）
};

/**
 * 邮件类型显示名称映射
 */
export const EmailTypeName = {
  [EmailType.CONFIRMATION]: '航班计划确认',
  [EmailType.PUBLISH]: '航班计划发布',
  [EmailType.GENERAL]: '通用邮件',
};

// ============ 发送选项常量 ============

/**
 * 发送选项类型枚举
 * 对应邮件编辑器底部的可配置选项
 */
export const SendingOptionType = {
  BCC_SENDING: 'bcc_sending',           // 密送发送（群发单显）
  READ_RECEIPT: 'read_receipt',           // 已读回执
  DELAYED_SENDING: 'delayed_sending',    // 延时发送
  // 未来可扩展其他选项
};

/**
 * 发送选项显示名称和描述
 */
export const SendingOptionLabel = {
  [SendingOptionType.BCC_SENDING]: {
    label: '群发单显',
    description: '为每个收件人分别发送一封单独的邮件，互不暴露邮箱地址',
  },
  [SendingOptionType.READ_RECEIPT]: {
    label: '已读回执',
    description: '请求收件人确认已读邮件（需要收件人邮箱客户端支持）',
  },
  [SendingOptionType.DELAYED_SENDING]: {
    label: '定时发送',
    description: '指定邮件发送时间',
  },
};

// ============ 邮件类型配置 ============

/**
 * 邮件类型完整配置
 *
 * 配置结构：
 * {
 *   name: 显示名称,
 *   options: {
 *     [optionType]: {
 *       default: boolean,      // 默认值
 *       required: boolean,     // 是否必须开启（强制）
 *       editable: boolean,      // 是否允许用户修改（默认根据required决定）
 *     }
 *   }
 * }
 *
 * 说明：
 * - 所有选项始终显示
 * - editable决定用户是否可以切换
 * - required=true时，editable自动为false（强制开启）
 */
export const EmailTypeConfig = {
  [EmailType.CONFIRMATION]: {
    name: EmailTypeName[EmailType.CONFIRMATION],
    options: {
      // 确认邮件：群发单显可关闭
      [SendingOptionType.BCC_SENDING]: {
        default: false,
        required: false,
        editable: true,
      },
      // 已读回执：可选
      [SendingOptionType.READ_RECEIPT]: {
        default: false,
        required: false,
        editable: true,
      },
    },
  },

  [EmailType.PUBLISH]: {
    name: EmailTypeName[EmailType.PUBLISH],
    options: {
      // 发布邮件：强制开启群发单显
      [SendingOptionType.BCC_SENDING]: {
        default: true,
        required: true,
        editable: false,  // 强制开启，用户不能切换
      },
      // 已读回执：可选
      [SendingOptionType.READ_RECEIPT]: {
        default: false,
        required: false,
        editable: true,
      },
    },
  },

  [EmailType.GENERAL]: {
    name: EmailTypeName[EmailType.GENERAL],
    options: {
      // 通用邮件：所有选项可选
      [SendingOptionType.BCC_SENDING]: {
        default: false,
        required: false,
        editable: true,
      },
      [SendingOptionType.READ_RECEIPT]: {
        default: false,
        required: false,
        editable: true,
      },
    },
  },
};

// ============ 辅助函数 ============

/**
 * 获取指定邮件类型和选项类型的配置
 * @param {string} emailType - 邮件类型常量
 * @param {string} optionType - 选项类型常量
 * @returns {Object|null} 选项配置对象，未找到返回null
 */
export const getOptionConfig = (emailType, optionType) => {
  const typeConfig = EmailTypeConfig[emailType];
  if (!typeConfig) return null;

  return typeConfig.options[optionType] || null;
};

/**
 * 获取所有发送选项（用于渲染）
 * @param {string} emailType - 邮件类型
 * @returns {Array} 所有选项配置 [{type, label, description, default, required, editable}]
 */
export const getAllOptions = (emailType) => {
  const typeConfig = EmailTypeConfig[emailType];
  if (!typeConfig) return [];

  return Object.entries(typeConfig.options)
    .map(([optionType, config]) => ({
      type: optionType,
      label: SendingOptionLabel[optionType]?.label || optionType,
      description: SendingOptionLabel[optionType]?.description || '',
      default: config.default,
      required: config.required,
      editable: config.editable,
    }));
};

/**
 * 获取选项的初始状态值（用于初始化表单）
 * @param {string} emailType - 邮件类型
 * @returns {Object} 初始值对象 { optionType: boolean }
 */
export const getInitialOptionValues = (emailType) => {
  const typeConfig = EmailTypeConfig[emailType];
  if (!typeConfig) return {};

  return Object.entries(typeConfig.options)
    .reduce((acc, [optionType, config]) => {
      acc[optionType] = config.default;
      return acc;
    }, {});
};

/**
 * 获取邮件类型的名称
 * @param {string} emailType - 邮件类型
 * @returns {string}
 */
export const getEmailTypeName = (emailType) => {
  return EmailTypeName[emailType] || emailType || '未知类型';
};

/**
 * 判断指定选项是否可编辑
 * @param {string} emailType - 邮件类型
 * @param {string} optionType - 选项类型
 * @returns {boolean} 是否可编辑
 */
export const isOptionEditable = (emailType, optionType) => {
  const config = getOptionConfig(emailType, optionType);
  return config?.editable ?? true;
};
